/**
 * بلاغُ أخطاء المتصفّح (29 سبتمبر 2026): كلُّ ما يقع في الواجهة — شاشةٌ لم تُحمَّل، خطأُ تصيير أمسكته
 * حدودُ الخطأ، `window.error`، وعدٌ مرفوض بلا معالج — يصل الخادمَ بصمةً (`POST /client-errors`)
 * فتراه إدارةُ المنصة في صحة التشغيل بدل انتظار الشكوى.
 *
 * ما **لا** يُرسل: أي معطًى شخصي. المسارُ يُرسل بنمطه (`/app/students/:id`)، والرسالةُ أولُ سطرٍ
 * مقنَّعٌ (بلا بريد ولا أرقامٍ طويلة ولا نصوصٍ مقتبسة — الخادمُ يعيد التقنيع احتياطاً)، والصفةُ
 * اسمُ الدور لا الشخص. ولا يُرسل الخطأُ نفسُه مرّتين في الجلسة، ولا أكثر من عشرين بلاغاً فيها —
 * عطلٌ يدور في حلقة لا يغرق الخادم.
 */
import { api } from './api';

export type ClientErrorKind = 'chunk' | 'render' | 'error' | 'rejection' | 'api';

const sent = new Set<string>();
let budget = 20;
let role: string | null = null;

/** الصفةُ الحالية (اسمُ الدور) — تضبطها القشرةُ عند الدخول وتُمحى عند الخروج */
export function setErrorReporterRole(r: string | null): void { role = r; }

export function routePattern(path: string): string {
  return path
    .split('?')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\b(RF|RC|AC)-\d+/g, ':ref')
    .replace(/\/\d+(?=\/|$)/g, '/:n')
    .slice(0, 300);
}

export function sanitizeMessage(raw: unknown): string {
  const text = raw instanceof Error ? `${raw.name}: ${raw.message}` : typeof raw === 'string' ? raw : (() => { try { return JSON.stringify(raw); } catch { return String(raw); } })();
  const first = (text ?? '').split('\n').map((l) => l.trim()).find((l) => l.length > 0) ?? 'unknown';
  return first
    .replace(/\S+@\S+/g, '***@***')
    .replace(/\d{4,}/g, '####')
    .replace(/"[^"]*"/g, '"…"')
    .replace(/'[^']*'/g, "'…'")
    .slice(0, 300);
}

export function buildId(): string {
  try { return typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'; } catch { return 'dev'; }
}

/** يرسل ولا يرمي ولا ينتظر — الإبلاغُ عن العطل لا يصير عطلاً ثانياً */
export function reportClientError(kind: ClientErrorKind, error: unknown, path = typeof location !== 'undefined' ? location.pathname : '/'): void {
  try {
    const message = sanitizeMessage(error);
    const route = routePattern(path);
    const key = `${kind}|${route}|${message}`;
    if (sent.has(key) || budget <= 0) return;
    sent.add(key);
    budget -= 1;
    void api.post('/client-errors', { kind, route, message, build: buildId(), role: role ?? undefined }, { anonymous: true, institutionId: null }).catch(() => undefined);
  } catch { /* لا شيء */ }
}

/** للاختبار: يعيد الحالةَ إلى أولها */
export function resetErrorReporter(): void { sent.clear(); budget = 20; role = null; }

let installed = false;
/** يُركَّب مرّةً عند الإقلاع: أخطاءُ النافذة والوعودُ المرفوضة. أخطاءُ التصيير تصل من `AppErrorBoundary`. */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    // أخطاءُ تحميل الموارد (صورةٌ 404) تصل هنا أيضاً بلا `error` — ليست أعطالَ كود
    if (!e.error && !e.message) return;
    reportClientError('error', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { status?: number; code?: string } | undefined;
    // ردودُ الخادم المرفوضة (401/403/404/422) قراراتٌ لا أعطال — تُسجَّل في الخادم أصلاً حين تكون 500
    if (r && typeof r === 'object' && typeof r.status === 'number' && r.status < 500) return;
    reportClientError('rejection', e.reason);
  });
}
